import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { WBSButton } from '@/common/WBSButton';
import {
  type BoardTarget,
  ModalParagraph,
  StudentBoardModal,
  StudentInfoBlock,
  targetLabel,
  useAdoptionErrorContent,
} from './StudentBoardModal';

interface PassOnStudentModalProps {
  /** The student to pass on. `null` renders nothing — this is the open switch. */
  studentId: number | null;
  onClose: () => void;
  /** Fired once the pass-on has landed, so the board can refresh behind it. */
  onPassedOn?: () => void;
}

/**
 * Legacy's two-step pass-on popup — `PassonStudentInteractivePopup.php:15-47`,
 * popups 21906 and 21907.
 *
 * Unlike adopt, the confirm step posts NO ids: legacy sends a CSV of the
 * still-available targets (`passon_info.php:59`) but the server releases the
 * whole group's claim regardless of what was sent
 * (`Models/WbsStudent.php:102-111`), so `POST /v3/adoption/pass-on` takes a bare
 * `user_id` and derives the set itself. The list here is therefore read-only.
 */
export function PassOnStudentModal({
  studentId,
  onClose,
  onPassedOn,
}: PassOnStudentModalProps) {
  const { t } = useTranslation();
  const [passedOnName, setPassedOnName] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [openedFor, setOpenedFor] = useState<number | null>(studentId);
  // Which student the modal is showing RIGHT NOW, readable from a settled
  // request — see AdoptStudentModal for why a settled pass-on must not write
  // its body into whatever modal is open by then.
  const openSession = useRef<number | null>(studentId);
  // Synchronous single-flight; `isPending` disables the button a render late.
  const submitting = useRef(false);

  // Reopening for a different student must not inherit the last one's success
  // body. Adjusting state during render (rather than in an effect) keeps the
  // first paint of the new modal correct.
  if (openedFor !== studentId) {
    setOpenedFor(studentId);
    setPassedOnName(null);
    setSubmitError(null);
  }

  useEffect(() => {
    openSession.current = studentId;
  }, [studentId]);

  // NOT `useStudentAdoptionInfo`, which is the ADOPT reading of this endpoint.
  // `mode=pass_on` selects legacy's `$is_passon` ladder — already-taken only —
  // so a lead this helper may not adopt still opens its confirm body, which is
  // what `POST /v3/adoption/pass-on` has always been willing to write
  // (`StudentBoardInteractivePopupAjax.php:28, :56`). Everything else about the
  // query is the hook's, and for the hook's reasons: `retry: false` because a
  // typed 4xx here is an answer and not a crash, and `enabled` goes false once
  // the pass-on lands so the settled payload stays readable behind the success
  // body instead of being refetched into a 404.
  const {
    data: info,
    isLoading,
    error,
  } = api.getStudentAdoptionInfo.useQuery(
    { path: { student_id: studentId ?? 0 }, query: { mode: 'pass_on' } },
    { enabled: passedOnName === null && studentId !== null, retry: false },
  );
  const { mutateAsync: passOnStudent, isPending } =
    api.passOnStudent.useMutation();

  const failure = submitError ?? error;
  const errorContent = useAdoptionErrorContent(
    failure,
    info?.display_name ?? '',
  );

  if (studentId === null) return null;

  if (passedOnName !== null) {
    return (
      <StudentBoardModal
        title={t('wbs.student-has-been-passed-on')}
        onClose={onClose}
        footer={
          <WBSButton alternate onClick={onClose}>
            {t('wbs.close')}
          </WBSButton>
        }
      >
        <ModalParagraph>
          {t('wbs.the-student-s-has-been-passed-on', { name: passedOnName })}
        </ModalParagraph>
      </StudentBoardModal>
    );
  }

  if (failure) {
    return (
      <StudentBoardModal
        title={errorContent.title ?? t('wbs.pass-this-student-to-the-others')}
        onClose={onClose}
        footer={
          <WBSButton alternate onClick={onClose}>
            {t('wbs.close')}
          </WBSButton>
        }
      >
        <ModalParagraph>{errorContent.body}</ModalParagraph>
      </StudentBoardModal>
    );
  }

  if (isLoading || !info) {
    return (
      <StudentBoardModal
        title={t('wbs.pass-this-student-to-the-others')}
        onClose={onClose}
      >
        <ModalParagraph>{t('wbs.loading')}</ModalParagraph>
      </StudentBoardModal>
    );
  }

  const handlePassOn = async (): Promise<void> => {
    if (submitting.current) return;
    submitting.current = true;
    const submittedFor = studentId;
    setSubmitError(null);
    try {
      const result = await passOnStudent({ body: { user_id: studentId } });
      // The server names the student it released, which is what the success
      // body prints; the confirm payload is stale by then.
      if (openSession.current === submittedFor) {
        setPassedOnName(result.display_name);
      }
    } catch (failed) {
      if (openSession.current === submittedFor) setSubmitError(failed);
      return;
    } finally {
      submitting.current = false;
    }
    onPassedOn?.();
  };

  return (
    <StudentBoardModal
      title={t('wbs.pass-this-student-to-the-others')}
      onClose={onClose}
      footer={
        <WBSButton
          onClick={() => {
            void handlePassOn();
          }}
          isLoading={isPending}
        >
          {t('wbs.pass-on-2')}
        </WBSButton>
      }
    >
      {/* Popup 21906's body, and the only place the user is told what pass-on
          does. It exists in all four languages (posts 21906/22207/25414/23033). */}
      <ModalParagraph>{t('wbs.pass-on-explainer')}</ModalParagraph>
      <StudentInfoBlock
        info={info}
        // AVAILABLE, not original: pass-on releases the still-claimed targets
        // and legacy's list drops the ones already passed on
        // (passon_info.php:32-52).
        targets={info.available_targets}
        renderTargets={(targets: readonly BoardTarget[]) => (
          <ul>
            {targets.map((target) => (
              <li key={target.target_id}>{targetLabel(target)}</li>
            ))}
          </ul>
        )}
      />
    </StudentBoardModal>
  );
}
